import React from 'react';
import { motion } from 'framer-motion';
import { FaCode, FaServer, FaPalette, FaMusic, FaCamera, FaPlane } from 'react-icons/fa';
import { usePageData } from '../hooks/useFirestoreData';
import '../styles/About.css';

/*
  Firestore doc: pages/about
  {
    bio: ["paragraph 1", "paragraph 2", "paragraph 3"],
    expertise: [
      { icon: "code", title: "Frontend Craft", desc: "..." },
      { icon: "server", title: "Backend Systems", desc: "..." },
      { icon: "palette", title: "Design Vision", desc: "..." }
    ],
    passions: [
      { icon: "music", label: "Music" },
      { icon: "camera", label: "Photography" },
      { icon: "plane", label: "Travel" }
    ],
    status: "Available for hire"
  }
*/

const ICON_MAP = {
  code: <FaCode />,
  server: <FaServer />,
  palette: <FaPalette />,
  music: <FaMusic />,
  camera: <FaCamera />,
  plane: <FaPlane />,
};

const DEFAULT_ABOUT = {
  bio: [
    "I'm a full-stack software engineer who thrives at the intersection of design and technology. Over the years, I've honed my craft across the entire web development spectrum — from sculpting responsive, animated frontends to engineering resilient, high-throughput backend systems.",
    "My philosophy is simple: great software should feel invisible. Users shouldn't wrestle with interfaces — they should glide through them. Every project I take on is an opportunity to push boundaries, challenge assumptions, and deliver something that genuinely makes a difference.",
    "Beyond the terminal, I find creative fuel in capturing moments through photography, getting lost in music, and exploring new corners of the world. Meeting people from different walks of life broadens my perspective and infuses my work with fresh ideas and empathy.",
  ],
  expertise: [
    { icon: 'code', title: 'Frontend Craft', desc: 'JavaScript, React, Vue, Angular — building interfaces that feel alive with fluid motion and pixel-perfect detail.' },
    { icon: 'server', title: 'Backend Systems', desc: 'Node.js, Express, MongoDB — architecting robust APIs and scalable server-side solutions that power seamless experiences.' },
    { icon: 'palette', title: 'Design Vision', desc: 'Photoshop, Adobe XD — translating creative concepts into polished visual designs with a keen eye for aesthetics.' },
  ],
  passions: [
    { icon: 'music', label: 'Music' },
    { icon: 'camera', label: 'Photography' },
    { icon: 'plane', label: 'Travel' },
  ],
  status: 'Available for hire',
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6, ease: [0.25, 0.8, 0.25, 1] },
  }),
};

function About() {
  const { data } = usePageData('about', DEFAULT_ABOUT);

  return (
    <section className="about page-wrapper">
      <div className="about-container">
        <motion.h1
          className="section-title"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          About Me
        </motion.h1>

        <div className="about-bio">
          <motion.div
            className="about-avatar"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            custom={1}
          >
            <div className="avatar-ring">
              <div className="avatar-inner">
                <span className="avatar-initials">AK</span>
              </div>
            </div>
            <div className="avatar-status">
              <span className="status-dot" />
              {data.status}
            </div>
          </motion.div>

          <div className="about-text">
            {data.bio.map((paragraph, i) => (
              <motion.p
                key={i}
                className={i === 0 ? 'bio-lead' : undefined}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                custom={i + 1}
              >
                {paragraph}
              </motion.p>
            ))}
          </div>
        </div>

        <div className="expertise-grid">
          {data.expertise.map((item, i) => (
            <motion.div
              key={i}
              className="glass-card expertise-card"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              custom={i + 1}
            >
              <div className="expertise-icon">
                {ICON_MAP[item.icon] || <FaCode />}
              </div>
              <h3 className="expertise-title">{item.title}</h3>
              <p className="expertise-desc">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="passions-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          custom={2}
        >
          <h2 className="passions-heading">Beyond Code</h2>
          <div className="passions-row">
            {data.passions.map((p, i) => (
              <motion.div
                key={i}
                className="passion-chip"
                whileHover={{ scale: 1.08, boxShadow: '0 0 20px rgba(0,212,255,0.3)' }}
              >
                {ICON_MAP[p.icon] || <FaCode />}
                <span>{p.label}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default About;
